import { useCallback, useRef, useState } from 'react';

import { useYoutubeCaptions } from '@/hooks/useYoutubeCaptions';
import { apiClient, type YoutubeCaption, type YoutubeSearchItem } from '@/services';
import { fillLiveSearchPage } from './liveSearchPaging';

const DEFAULT_PAGE_SIZE = 20;
const MAX_QUERY_LENGTH = 200;

interface UseVideoSearchOptions {
  pageSize?: number;
}

interface UseVideoSearchResult {
  submittedQuery: string;
  items: YoutubeSearchItem[];
  activeItem: YoutubeSearchItem | undefined;
  activeIndex: number;
  /** How many clips are loaded so far; with {@link hasMore} set it is a lower bound, not a total. */
  matchCount: number;
  hasMore: boolean;
  canMoveNext: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string;
  loadMoreError: string;
  captions: YoutubeCaption[];
  captionsLoading: boolean;
  captionsError: string;
  search: (query: string) => Promise<void>;
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  goToIndex: (index: number) => void;
}

/**
 * Transcript search with one result in view at a time: paginates lazily as the
 * reader walks forward and keeps the active video's subtitles cached. The search walks bounded
 * windows of the corpus, so a page is filled from as many windows as it takes.
 */
export function useVideoSearch({ pageSize = DEFAULT_PAGE_SIZE }: UseVideoSearchOptions = {}): UseVideoSearchResult {
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [items, setItems] = useState<YoutubeSearchItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const requestVersionRef = useRef(0);

  const activeItem = items[activeIndex];
  const {
    captions,
    loading: captionsLoading,
    error: captionsError,
  } = useYoutubeCaptions(activeItem?.videoId);
  const canMoveNext = activeIndex < items.length - 1 || hasMore;

  const search = useCallback(async (value: string) => {
    const query = value.trim();
    if (!query || query.length > MAX_QUERY_LENGTH) return;

    const requestVersion = ++requestVersionRef.current;
    setSubmittedQuery(query);
    setLoading(true);
    setLoadingMore(false);
    setError('');
    setLoadMoreError('');
    setItems([]);
    setActiveIndex(0);
    setNextCursor(undefined);
    setHasMore(false);

    try {
      const page = await fillLiveSearchPage({
        pageSize,
        fetchWindow: (cursor, limit) => apiClient.searchYoutube(query, cursor, limit),
        getKey: (item) => String(item.sentenceId),
      });
      if (requestVersion !== requestVersionRef.current) return;

      setItems(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch {
      if (requestVersion === requestVersionRef.current) {
        setError('Unable to search videos. Please try again.');
      }
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [pageSize]);

  const goToNext = async () => {
    if (activeIndex < items.length - 1) {
      setActiveIndex((index) => index + 1);
      setLoadMoreError('');
      return;
    }

    if (!hasMore || !nextCursor || loadingMore) return;

    const requestVersion = requestVersionRef.current;
    const nextIndex = items.length;
    setLoadingMore(true);
    setLoadMoreError('');
    try {
      const page = await fillLiveSearchPage({
        cursor: nextCursor,
        pageSize,
        fetchWindow: (cursor, limit) => apiClient.searchYoutube(submittedQuery, cursor, limit),
        getKey: (item) => String(item.sentenceId),
        knownKeys: items.map((item) => String(item.sentenceId)),
      });
      if (requestVersion !== requestVersionRef.current) return;

      const nextItems = page.items;
      setItems((current) => [...current, ...nextItems]);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      if (nextItems.length > 0) {
        setActiveIndex(nextIndex);
      }
    } catch {
      if (requestVersion === requestVersionRef.current) {
        setLoadMoreError('Unable to load the next result. Please try again.');
      }
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoadingMore(false);
      }
    }
  };

  const goToPrevious = () => {
    setActiveIndex((index) => Math.max(0, index - 1));
    setLoadMoreError('');
  };

  const goToIndex = (index: number) => {
    if (index < 0 || index >= items.length) return;
    setActiveIndex(index);
    setLoadMoreError('');
  };

  return {
    submittedQuery,
    items,
    activeItem,
    activeIndex,
    matchCount: items.length,
    hasMore,
    canMoveNext,
    loading,
    loadingMore,
    error,
    loadMoreError,
    captions,
    captionsLoading,
    captionsError,
    search,
    goToNext,
    goToPrevious,
    goToIndex,
  };
}
